import { useState } from "react";
import { resolveImagePath } from "@/utils/imageMap";
import navigationData from "@/data/navigation.json";
import type { NavigationData } from "@/data/types";

const navigation = navigationData as NavigationData;

interface HeaderProps {
  className?: string;
}

export function Header({ className = "" }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<number | null>(null);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleSubmenu = (index: number) => {
    setOpenSubmenu(openSubmenu === index ? null : index);
  };

  return (
    <div className={`${className}`}>
      {/* Top Contact Bar */}
      <div className="bg-white border-b border-gray-200 py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex space-x-6 text-sm text-black">
              <span className="font-semibold">{navigation.topContact.phone}</span>
              <a 
                href={`mailto:${navigation.topContact.email}`}
                className="text-blue-600 hover:text-blue-800"
              >
                {navigation.topContact.email}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex-shrink-0">
              <a href="/" className="flex items-center">
                <img
                  src={resolveImagePath("/images/asi-logo.png")}
                  alt="Choose ASI"
                  className="h-12 w-auto"
                />
              </a>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-8">
              {navigation.mainNavigation.map((item, index) => (
                <div key={index} className="relative group">
                  <a
                    href={item.href}
                    className="text-black hover:text-blue-600 px-3 py-2 text-sm font-semibold transition-colors"
                    {...(item.external && { target: "_blank", rel: "noopener noreferrer" })}
                  >
                    {item.label}
                  </a>
                  
                  {/* Desktop Submenu */}
                  {item.submenu && item.submenu.length > 0 && (
                    <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <div className="py-2">
                        {item.submenu.map((subItem, subIndex) => (
                          <a
                            key={subIndex}
                            href={subItem.href}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600"
                            {...(subItem.external && { target: "_blank", rel: "noopener noreferrer" })}
                          >
                            {subItem.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Main menu"
            >
              <svg
                className={`h-6 w-6 ${isMobileMenuOpen ? 'hidden' : 'block'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <svg
                className={`h-6 w-6 ${isMobileMenuOpen ? 'block' : 'hidden'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Mobile Navigation */}
          <div className={`lg:hidden ${isMobileMenuOpen ? 'block' : 'hidden'} pb-4 border-t border-gray-200 mt-4`}>
            <div className="pt-4 space-y-1">
              {navigation.mainNavigation.map((item, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between">
                    <a
                      href={item.href}
                      className="block px-3 py-2 text-base font-medium text-black hover:text-blue-600 hover:bg-gray-50 rounded-md flex-1"
                      {...(item.external && { target: "_blank", rel: "noopener noreferrer" })}
                    >
                      {item.label}
                    </a>
                    {item.submenu && item.submenu.length > 0 && (
                      <button
                        onClick={() => toggleSubmenu(index)}
                        className="p-2 text-gray-400 hover:text-gray-500"
                      >
                        <svg
                          className={`h-4 w-4 transform transition-transform ${openSubmenu === index ? 'rotate-180' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    )}
                  </div>
                  
                  {/* Mobile Submenu */}
                  {item.submenu && item.submenu.length > 0 && openSubmenu === index && (
                    <div className="ml-4 mt-2 space-y-1">
                      {item.submenu.map((subItem, subIndex) => (
                        <a
                          key={subIndex}
                          href={subItem.href}
                          className="block px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-md"
                          {...(subItem.external && { target: "_blank", rel: "noopener noreferrer" })}
                        >
                          {subItem.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </header>
    </div>
  );
}